/**
 * UpsellToast — iter-120 B4
 *
 * Listens for `plan-limit-exceeded` window events dispatched by the
 * axios 402 interceptor (lib/api.js) and renders a rich Sonner toast
 * with the structured upsell payload.
 *
 * Mounted once globally in AppShell so every authenticated page
 * automatically gets feedback when the user trips a plan boundary.
 *
 * Dedup: the same (kind, key) pair within 5s collapses into one toast
 * so a rapid burst of denied calls doesn't spam the UI.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, Lock, Gauge } from "lucide-react";


const KIND_ICONS = {
    meter:    Gauge,
    limit:    AlertTriangle,
    feature:  Lock,
    access:   Lock,
    throttle: Gauge,
};


export default function UpsellToast() {
    const nav = useNavigate();
    const lastSeen = useRef({}); // dedup window: { "kind:key": ts }

    useEffect(() => {
        const handler = (e) => {
            const p = e.detail || {};
            if (!p || p.error !== "plan_limit_exceeded") return;

            // 5s dedup window per (kind, key) pair
            const dedupKey = `${p.kind}:${p.key}`;
            const now = Date.now();
            const last = lastSeen.current[dedupKey] || 0;
            if (now - last < 5000) return;
            lastSeen.current[dedupKey] = now;

            const Icon = KIND_ICONS[p.kind] || Sparkles;
            const isShadow = !!p.shadow;
            const title = isShadow
                ? `[Shadow mode] Would have blocked · ${p.key}`
                : `Plan limit reached · ${p.key}`;

            toast(
                title,
                {
                    description: p.message,
                    icon: <Icon className="h-4 w-4" />,
                    duration: 8000,
                    action: {
                        label: "Upgrade →",
                        onClick: () => nav(p.upgrade_url || "/billing"),
                    },
                    // testid hook for the testing agent
                    id: `upsell-${dedupKey}`,
                    className: isShadow ? "border-amber-500/40" : "border-destructive/40",
                },
            );
        };
        window.addEventListener("plan-limit-exceeded", handler);
        return () => window.removeEventListener("plan-limit-exceeded", handler);
    }, [nav]);

    return null;
}
